import { Auth } from "aws-amplify";
import {
  fetchInvoicesSuccess,
  fetchInvoicesError,
  fetchInvoicesStart,
  updateInvoicesAddedToCart,
  updateInvoicesInProgress,
  updateInvoicesNeedsReview,
} from "./invoicesSlice";
import { autoAddSuccess, autoAddError, autoAddStart } from "./autoAddSlice";
import {
  fetchCartSuccess,
  fetchCartFailure,
  fetchDirectCartSuccess,
  tagCartItemsWithInvoiceIDs,
} from "./cartsSlice";
import {
  fetchPurchaseOrdersStart,
  fetchPurchaseOrderVendorUpdate,
  fetchPurchaseOrdersSuccess,
  fetchPurchaseOrdersError,
} from "./purchaseOrdersSlice";
import { fetchProductSuccess, fetchProductFailure } from "./productsSlice";
import { addVendorShippingInfo } from "utilities/addShippingInfo";
import parseReceivedMessage from "./parseReceivedMessage";
import posthog from "posthog-js";
import { datadogLogs } from "@datadog/browser-logs";

const WS_API = {
  development: "wss://api-dev.dgiapparel.com/ws",
  staging: "wss://api-staging.dgiapparel.com/ws",
  production: "wss://api-prod.dgiapparel.com/ws",
};

const getEnvironment = () => {
  if (import.meta.env.VITE_LOCAL_DEV === "true") {
    return "development";
  }
  if (import.meta.env.VITE_IS_STAGING === "true") {
    return "staging";
  }
  return "production";
};

const SOCKET_API =
  import.meta.env.VITE_LOCAL_DEV === "true"
    ? "ws://127.0.0.1:8000/ws"
    : WS_API[getEnvironment()];

const dgiApiWebSocketMiddleware = (store) => {
  let socket = null;
  let last_socket_user_id = null;
  let socket_id = null; // Add socket ID tracking
  let pendingMessages = [];
  let retryCount = 0;
  let maxRetries = 5;
  let baseDelay = 1000; // Initial delay of 1 second
  let heartbeatIntervalId = null; // Store the heartbeat interval ID

  // Add message buffer to store chunks
  const messageBuffers = new Map();

  const createSocket = (user_id, token) => {
    socket_id = `socket-${Date.now()}`;
    socket = new WebSocket(SOCKET_API + `?user_id=${user_id}&token=${token}`);
    datadogLogs.logger.info("Created new socket instance", { socket_id });
    return socket;
  };

  // Helper to handle complete message after reassembly
  const handleCompleteMessage = (message) => {
    const { dispatch } = store;
    // Handle new error message format
    if (message.error !== undefined) {
      switch (message.action) {
        case "get_invoices":
          dispatch(fetchInvoicesError(message.error));
          return;
        case "auto_add":
          dispatch(autoAddError(message.error?.errorMessage));
          return;
        case "get_purchase_orders":
          dispatch(fetchPurchaseOrdersError(message.error));
          return;
        default:
          // For unknown actions, dispatch a general websocket error
          dispatch({
            type: "DGI_API_WEBSOCKET_ERROR",
            payload: { error: message.error, messageId: message.message_id },
          });
          return;
      }
    }

    // Explicitly handle preview_carts action
    if (message.action === "preview_carts") {
      // Dispatch directly to composerSlice
      dispatch({ type: "DGI_API_WEBSOCKET_MESSAGE", payload: message });
      return; // Return early to prevent further processing
    }

    // Handle invoice messages
    if (message.invoices !== undefined) {
      dispatch(fetchInvoicesSuccess(message.invoices || []));
    }

    // Handle auto-add messages
    if (
      message.vendor_po_items !== undefined ||
      message.items_not_found !== undefined
    ) {
      if (message.error) {
        dispatch(autoAddError(message.error?.errorMessage));
      } else {
        // Update carts with the response data
        for (const [vendorCode, respData] of Object.entries(
          message.vendor_po_items,
        )) {
          dispatch(
            fetchCartSuccess({
              vendorCode,
              cartData: respData,
            }),
          );
        }
      }
      // Update items not found
      dispatch(autoAddSuccess(message));
      // Update invoices added to cart
      dispatch(updateInvoicesAddedToCart());
    }

    // Handle purchase order messages
    if (message.purchase_orders !== undefined) {
      dispatch(fetchPurchaseOrdersSuccess(message.purchase_orders || []));
    }

    if (message.items_not_found) {
      // Get unique invoice IDs from items that need review
      const needsReviewInvoiceIds = message.items_not_found.map(
        (groupedItems) => {
          return groupedItems[0]?.invoice_id;
        },
      );
      dispatch(updateInvoicesNeedsReview(needsReviewInvoiceIds));
    }

    // Dispatch general websocket message for other handlers
    dispatch({ type: "DGI_API_WEBSOCKET_MESSAGE", payload: message });
  };

  const onOpen = () => {
    console.log("DGI API WebSocket connected");
    console.log(socket);
    datadogLogs.logger.info("WebSocket connected", {
      readyState: socket.readyState,
      pendingMessages: pendingMessages.length,
    });

    // Reset retry count on successful connection
    retryCount = 0;

    // Start periodic heartbeat every 5 minutes
    heartbeatIntervalId = setInterval(
      () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          const heartbeatMsg = {
            action: "heartbeat",
            timestamp: Date.now(),
          };
          socket.send(JSON.stringify(heartbeatMsg));
          console.log("Heartbeat sent:", heartbeatMsg);
        }
      },
      5 * 60 * 1000, // 5 minutes in ms
    );

    // Ensure socket is fully open before sending messages
    if (socket.readyState === WebSocket.OPEN) {
      // Send pending messages when the connection is opened
      console.log(`Sending ${pendingMessages.length} pending messages`);
      pendingMessages.forEach((message) => {
        socket.send(JSON.stringify(message));
      });
      pendingMessages = [];
    } else {
      console.log("Socket not yet ready, keeping messages in pending queue");
      datadogLogs.logger.info(
        "Socket not ready - keeping messages in pending queue",
        {
          readyState: socket.readyState,
        },
      );
    }
  };

  const onClose = async (event) => {
    console.log("DGI API WebSocket disconnected");
    console.log(event);
    datadogLogs.logger.info("WebSocket disconnected", {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
      socketId: socket_id,
    });

    // Clear the heartbeat interval
    if (heartbeatIntervalId) {
      clearInterval(heartbeatIntervalId);
      heartbeatIntervalId = null;
    }

    if (
      event?.reason !== "disconnect event connection close" &&
      last_socket_user_id != null
    ) {
      if (retryCount < maxRetries) {
        const delay = Math.min(baseDelay * Math.pow(2, retryCount), 30000); // Cap at 30 seconds
        console.log(
          `DGI API WebSocket reconnecting in ${delay}ms (attempt ${
            retryCount + 1
          }/${maxRetries})`,
        );
        datadogLogs.logger.info(`Scheduling reconnect`, {
          attempt: retryCount + 1,
          maxRetries,
          delayMs: delay,
          currentSocketId: socket_id,
        });
        retryCount++;

        setTimeout(async () => {
          console.log("DGI API WebSocket reconnecting");
          console.log("Attempting reconnection");
          const session = await Auth.currentSession();
          const jwtToken = session.idToken.jwtToken;
          socket = createSocket(last_socket_user_id, jwtToken);
          socket.onopen = onOpen;
          socket.onclose = onClose;
          socket.onmessage = onMessage;
        }, delay);
      } else {
        datadogLogs.logger.info(
          "DGI API WebSocket Max retry attempts reached - giving up",
        );
      }
    }
  };

  const onMessage = (event) => {
    const message = JSON.parse(event.data);
    const state = store.getState(); // Get current state for message ID verification

    // Handle heartbeat acknowledgement
    if (message.type === "heartbeat_ack") {
      console.log("Received heartbeat ack from server:", message);
      return;
    }

    // Handle chunked messages
    if (message.chunked === true) {
      console.log("Received chunked message");
      datadogLogs.logger.info("Received chunked message start", {
        messageId: message.message_id,
        action: message.action,
        totalChunks: message.total_chunks,
        totalSize: message.total_size,
      });
      // Initialize buffer for new chunked message
      messageBuffers.set(message.message_id, {
        chunks: new Array(message.total_chunks),
        received: 0,
        totalSize: message.total_size,
        action: message.action, // Store the action to identify message type later
      });
      return;
    }

    // Handle message chunk
    if (message.chunk_index !== undefined) {
      const buffer = messageBuffers.get(message.message_id);
      if (buffer) {
        // Store chunk
        buffer.chunks[message.chunk_index] = message.data;
        buffer.received++;

        console.log("Received message chunk", {
          messageId: message.message_id,
          chunkIndex: message.chunk_index,
          receivedChunks: buffer.received,
          totalChunks: message.total_chunks,
        });

        // Check if all chunks received
        if (buffer.received === message.total_chunks) {
          try {
            // Reassemble and parse complete message
            const completeMessage = JSON.parse(buffer.chunks.join(""));

            // Add the original action to the complete message if not present
            if (!completeMessage.action && buffer.action) {
              completeMessage.action = buffer.action;
            }

            // Log the reassembled message action for debugging
            console.log(
              "Reassembled complete message with action:",
              completeMessage.action,
            );
            datadogLogs.logger.info("Reassembled complete message", {
              messageId: message.message_id,
              action: completeMessage.action,
            });

            // Check the action to determine correct processing path
            if (completeMessage.action === "get_carts") {
              // Process actual cart data through parseReceivedMessage
              const additionalStoreData = getAdditionalStoreData(
                completeMessage.action,
                completeMessage.vendorCode,
              );
              parseReceivedMessage(
                store.dispatch,
                completeMessage,
                additionalStoreData,
              );
            } else if (completeMessage.action === "preview_carts") {
              // Process preview cart data directly through the composerSlice
              handleCompleteMessage(completeMessage);
            } else {
              // Handle other types of complete messages
              handleCompleteMessage(completeMessage);
            }
          } catch (error) {
            console.error("Error processing chunked message:", error);
            datadogLogs.logger.error("Error processing chunked message", {
              messageId: message.message_id,
              error: error.message,
            });
          } finally {
            // Clean up buffer
            messageBuffers.delete(message.message_id);
          }
        }
      }
      return;
    }

    // Handle regular non-chunked messages
    datadogLogs.logger.info("Received complete message", {
      action: message.action,
      messageId: message.message_id,
    });

    // Check for composer-related messages first (preview_carts, sync_composed_carts)
    if (
      message.action === "preview_carts" ||
      message.action === "sync_composed_carts"
    ) {
      // Try to handle it with parseReceivedMessage and pass the current state
      const handled = parseReceivedMessage(store.dispatch, message, state);

      // If it was handled by the parseReceivedMessage function, we can return
      if (handled === true) {
        return;
      }
    }

    // Handle legacy websocket messages using parseReceivedMessage
    if (message.action === "fetch_products" || message.action === "get_carts") {
      // Process product and cart data through parseReceivedMessage
      const additionalStoreData = getAdditionalStoreData(
        message.action,
        message.vendorCode,
      );
      parseReceivedMessage(store.dispatch, message, additionalStoreData);
    } else if (message.action === "preview_carts") {
      // Process preview cart data through handleCompleteMessage
      handleCompleteMessage(message);
    } else {
      // Handle all other message types
      handleCompleteMessage(message);
    }
  };

  const onError = (event) => {
    console.error("DGI API WebSocket error:", event);
    datadogLogs.logger.error("WebSocket error occurred", {
      socketId: socket_id,
      readyState: socket?.readyState,
      timestamp: new Date().toISOString(),
    });
  };

  const getAdditionalStoreData = (action, vendorCode) => {
    if (action === "fetch_products") {
      const state = store.getState();
      return {
        est_delivery_days:
          state?.user?.user?.Distributors?.[vendorCode]?.est_delivery_days,
      };
    } else if (action === "get_carts") {
      const state = store.getState();
      return {
        auto_add_line_items: state?.autoAdd?.line_items,
      };
    }
    return null;
  };

  return (next) => (action) => {
    switch (action.type) {
      case "DGI_API_WEBSOCKET_CONNECT":
      case "WEBSOCKET_CONNECT":
        const user_id = action.payload?.user_id;
        if (last_socket_user_id != null && user_id === last_socket_user_id) {
          break;
        }
        if (socket !== null) {
          console.log("Closing existing socket connection", {
            oldSocketId: socket_id,
          });
          console.log(socket);
          console.log(last_socket_user_id);
          socket.close(1000, "connection close");
        }
        // Get JWT token and add it to the WebSocket URL
        Auth.currentSession().then((session) => {
          const jwtToken = session.idToken.jwtToken;
          socket = createSocket(user_id, jwtToken);
          last_socket_user_id = user_id;
          socket.onopen = onOpen;
          socket.onclose = onClose;
          socket.onmessage = onMessage;
          socket.onerror = onError;
        });
        break;

      case "DGI_API_WEBSOCKET_DISCONNECT":
      case "WEBSOCKET_DISCONNECT":
        console.log("Received disconnect action");
        if (socket !== null) {
          socket.close(1000, "disconnect event connection close");
        }
        socket = null;
        last_socket_user_id = null;
        pendingMessages = [];
        // Clear message buffers on disconnect
        messageBuffers.clear();
        break;

      case "SET_COMPOSER_MESSAGE_ID":
        // Handle setting the composer message ID
        return next(action);
        break;

      case "DGI_API_WEBSOCKET_SEND":
      case "WEBSOCKET_SEND_MESSAGE":
        if (action.payload.action === "get_invoices") {
          store.dispatch(fetchInvoicesStart());
        } else if (action.payload.action === "auto_add") {
          // Add message_id for chunked responses
          const message_id = `${action.payload.action}-${Date.now()}`;
          action.payload.message_id = message_id;

          store.dispatch(autoAddStart());
          const partialInvoiceIds = action.payload.partial_invoice_ids.map(
            (invoiceObj) => invoiceObj.invoice_id,
          );
          store.dispatch(
            updateInvoicesInProgress([
              ...action.payload.invoice_ids,
              ...partialInvoiceIds,
            ]),
          );

          // Get the current session and add the access token to the payload
          Auth.currentSession().then((session) => {
            const payload = {
              ...action.payload,
              authorization: session.getAccessToken().getJwtToken(),
            };
            if (socket === null || socket.readyState !== WebSocket.OPEN) {
              pendingMessages.push(payload);
            } else {
              socket.send(JSON.stringify(payload));
            }
          });
          break;
        } else if (action.payload.action === "get_purchase_orders") {
          store.dispatch(fetchPurchaseOrdersStart());
        }
        if (socket === null || socket.readyState !== WebSocket.OPEN) {
          // If the connection is not open, add the message to the pending messages array
          datadogLogs.logger.info("Queueing message - socket not ready", {
            action: action.payload.action,
            socketState: socket?.readyState,
            messageId: action.payload.message_id,
          });
          pendingMessages.push(action.payload);
        } else {
          // If the connection is open, send the message immediately
          datadogLogs.logger.info("Sending message", {
            action: action.payload.action,
            messageId: action.payload.message_id,
          });
          socket.send(JSON.stringify(action.payload));
        }
        break;

      default:
        return next(action);
    }
  };
};

export default dgiApiWebSocketMiddleware;
